// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { setRememberMeToken } from 'utils/local-storage';
import { setAppLanguage } from 'utils/i18n';
import { PRIVATE, ADMIN } from 'router/routes';
import { useSetToken, useUser } from 'utils/contexts/app-context';
import {
  canAccessSecuritySettings,
  canManageAccountSettings,
} from 'utils/accountSettingsPermissions';
import { clearAll } from 'state/ducks/global';
import ImagePlaceHolder from 'components/image-placeholder';
import { Flags } from 'providers/FlagsProvider';
import FeatureFlags from 'utils/feature-flags';
import styles from './styles.module.scss';
import {
  Avatar,
  Box,
  Divider,
  Menu,
  MenuItem,
  MenuList,
  Switch,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TuneIcon from '@mui/icons-material/Tune';
import SyncIcon from '@mui/icons-material/Sync';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import { useDarkMode } from 'providers/DarkModeProvider';
import { Auth } from 'aws-amplify';
import ContactUsDialog, { getHelpLegalUrls } from 'components/HelpMenu/ContactUsDialog';

type Props = {
  picture: ?string,
  clearState: () => void,
};

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
];

const ProfilePictureWithDropdown = ({
  picture,
  clearState,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const setToken = useSetToken();
  const user = useUser();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [contactOpen, setContactOpen] = React.useState(false);
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { toggleDarkMode } = useDarkMode();
  const canManageSettings = canManageAccountSettings(user);
  const canAccessSecurity = canAccessSecuritySettings(user);
  const { termsUrl, privacyUrl } = getHelpLegalUrls(i18n.language);

  const currentLanguage = i18n.language?.includes('fr') ? 'fr' : 'en';

  const isOpen = Boolean(anchorEl);
  const displayName = user?.firstName?.trim() || '';
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

  const isNavActive = (path) => {
    if (path === PRIVATE.dashboard) {
      return (
        location.pathname === PRIVATE.dashboard ||
        location.pathname === `${PRIVATE.dashboard}/`
      );
    }
    if (path === PRIVATE.profile) {
      return location.pathname === PRIVATE.profile;
    }
    return location.pathname === path;
  };

  const showUserManagement = user && (user.isAdmin || user.accessLevel === 'LEAD');
  const showPlans = user && user.isAdmin;
  const showIntegrations = user && user.isAdmin;

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const goTo = (path) => {
    navigate(path);
    handleClose();
  };

  const onUsersClick = () => {
    if (user.accessLevel === 'LEAD') {
      goTo(`/admin/team/${user.team_id}`);
    } else {
      goTo(ADMIN.delegates);
    }
  };

  const onLogout = async () => {
    try {
      await Auth.signOut({ global: true });
    } catch (err) {
      console.log('Cognito signOut not needed or failed:', err);
    }

    setRememberMeToken(null);
    setToken(null);
    clearState();
    localStorage.removeItem('filtering');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    sessionStorage.setItem('justLoggedOut', 'true');

    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    handleClose();
    window.location.href = '/login';
  };

  const onSelectLanguage = (languageCode) => {
    if (languageCode === currentLanguage) return;
    void setAppLanguage(languageCode);
  };

  const menuPaperSx = {
    width: 280,
    maxWidth: 'calc(100vw - 24px)',
    maxHeight: 'calc(100vh - 24px)',
    mt: 1,
    borderRadius: 2,
    overflowY: 'auto',
    overflowX: 'hidden',
  };

  const menuItemSx = {
    py: 1,
    px: 2,
    gap: 1.5,
    fontSize: 14,
    textTransform: 'none',
    '& .MuiSvgIcon-root': {
      fontSize: 20,
      color: 'text.secondary',
    },
    '&.Mui-selected': {
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(100, 181, 246, 0.12)' : 'rgba(25, 118, 210, 0.08)',
      fontWeight: 600,
      '& .MuiSvgIcon-root': {
        color: 'primary.main',
      },
    },
    '&.Mui-selected:hover': {
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(100, 181, 246, 0.18)' : 'rgba(25, 118, 210, 0.12)',
    },
  };

  const preferenceItemSx = {
    ...menuItemSx,
    justifyContent: 'space-between',
  };

  const renderSectionHeader = (label) => (
    <Typography
      component="div"
      sx={{
        px: 2,
        pt: 1,
        pb: 0.5,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
  );

  const renderNavItem = (path, label, Icon) => (
    <MenuItem
      key={path}
      selected={isNavActive(path)}
      onClick={() => goTo(path)}
      sx={menuItemSx}
    >
      <Icon fontSize="small" />
      {label}
    </MenuItem>
  );

  const renderLanguageToggle = () => (
    <Box
      role="group"
      aria-label={t('glossary:nouns.menu_language')}
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: 'inline-flex',
        flexShrink: 0,
        bgcolor: 'action.hover',
        borderRadius: 1,
        p: 0.25,
      }}
    >
      {LANGUAGE_OPTIONS.map((option) => {
        const selected = currentLanguage === option.code;
        return (
          <Box
            key={option.code}
            component="button"
            type="button"
            aria-pressed={selected}
            onClick={() => onSelectLanguage(option.code)}
            sx={{
              border: 'none',
              cursor: 'pointer',
              px: 1.25,
              py: 0.5,
              fontSize: 12,
              lineHeight: 1.2,
              fontWeight: selected ? 600 : 400,
              borderRadius: 0.75,
              bgcolor: selected ? 'background.paper' : 'transparent',
              color: selected ? 'primary.main' : 'text.secondary',
              boxShadow: selected ? 1 : 0,
              fontFamily: 'inherit',
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <>
      <div className={styles.profile_picture_holder}>
        <div
          className={styles.profile_trigger}
          onClick={handleOpen}
          id="account_settings_circle"
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleOpen(event);
            }
          }}
        >
          <div className={styles.image_wrapper}>
            <ImagePlaceHolder src={picture} />
          </div>
          {displayName && (
            <span className={styles.profile_name}>{displayName}</span>
          )}
        </div>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: menuPaperSx,
            elevation: 8,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={picture || undefined}
            alt=""
            sx={{ width: 44, height: 44, bgcolor: 'primary.light' }}
          >
            {(user?.firstName?.[0] || user?.email?.[0] || '?').toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {fullName && (
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {fullName}
              </Typography>
            )}
            {user?.email && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {user.email}
              </Typography>
            )}
            {user?.company?.name && (
              <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.25 }}>
                {user.company.name}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider />

        <MenuList dense disablePadding sx={{ py: 0.5 }}>
          {renderNavItem(
            PRIVATE.dashboard,
            t('glossary:nouns.profile_menu_dashboard'),
            DashboardOutlinedIcon
          )}
        </MenuList>

        <Divider sx={{ my: 0.5 }} />

        {renderSectionHeader(t('nouns.profile_menu_account'))}
        <MenuList dense disablePadding sx={{ py: 0.5 }}>
          {renderNavItem(PRIVATE.profile, t('nouns.profile'), PersonOutlineIcon)}
          {canAccessSecurity &&
            renderNavItem(PRIVATE.profileSecurity, t('nouns.security_settings'), LockOutlinedIcon)}
        </MenuList>

        <Divider sx={{ my: 0.5 }} />

        {renderSectionHeader(t('glossary:nouns.profile_menu_settings'))}
        <MenuList dense disablePadding sx={{ py: 0.5 }}>
          {renderNavItem(
            PRIVATE.profileValidations,
            t('nouns.validation_plural'),
            TuneIcon
          )}

          {canManageSettings &&
            renderNavItem(
              PRIVATE.profileLiveUpdates,
              t('nouns.live_validation'),
              SyncIcon
            )}

          {showUserManagement && (
            <MenuItem onClick={onUsersClick} sx={menuItemSx}>
              <GroupOutlinedIcon fontSize="small" />
              {t('nouns.user_management')}
            </MenuItem>
          )}

          {canManageSettings &&
            renderNavItem(
              PRIVATE.usage,
              t('glossary:nouns.profile_menu_subscription'),
              BarChartOutlinedIcon
            )}

          <Flags authorizedFlags={[FeatureFlags.AllowBilling]}>
            {showPlans && (
              <MenuItem onClick={() => goTo(ADMIN.plans)} sx={menuItemSx}>
                <PaymentOutlinedIcon fontSize="small" />
                {t('nouns.plans_and_payments')}
              </MenuItem>
            )}
          </Flags>

          <Flags authorizedFlags={[FeatureFlags.AllowIntegrations]}>
            {showIntegrations && (
              <MenuItem onClick={() => goTo(ADMIN.integrations)} sx={menuItemSx}>
                <ExtensionOutlinedIcon fontSize="small" />
                {t('nouns.integrations')}
              </MenuItem>
            )}
          </Flags>
        </MenuList>

        <Divider sx={{ my: 0.5 }} />

        {renderSectionHeader(t('nouns.profile_menu_preferences'))}
        <MenuList dense disablePadding sx={{ py: 0.5 }}>
          <MenuItem
            sx={preferenceItemSx}
            disableRipple
            onClick={(event) => event.stopPropagation()}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <LanguageOutlinedIcon fontSize="small" />
              {t('glossary:nouns.menu_language')}
            </Box>
            {renderLanguageToggle()}
          </MenuItem>

          <MenuItem
            sx={preferenceItemSx}
            disableRipple
            onClick={(event) => event.stopPropagation()}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <DarkModeOutlinedIcon fontSize="small" />
              {t('nouns.dark_mode')}
            </Box>
            <Switch
              size="small"
              checked={theme.palette.mode === 'dark'}
              onChange={() => toggleDarkMode()}
              onClick={(event) => event.stopPropagation()}
              inputProps={{ 'aria-label': t('nouns.dark_mode') }}
            />
          </MenuItem>
        </MenuList>

        <Divider sx={{ my: 0.5 }} />

        {renderSectionHeader(t('glossary:nouns.profile_menu_help_and_legal'))}
        <MenuList dense disablePadding sx={{ py: 0.5 }}>
          <MenuItem
            onClick={() => {
              handleClose();
              setContactOpen(true);
            }}
            sx={menuItemSx}
          >
            <ContactSupportOutlinedIcon fontSize="small" />
            {t('sentences:help_menu.support')}
          </MenuItem>
          <MenuItem
            component="a"
            href={termsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
            sx={menuItemSx}
          >
            <DescriptionOutlinedIcon fontSize="small" />
            {t('sentences:help_menu.terms')}
          </MenuItem>
          <MenuItem
            component="a"
            href={privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
            sx={menuItemSx}
          >
            <PolicyOutlinedIcon fontSize="small" />
            {t('sentences:help_menu.privacy')}
          </MenuItem>
        </MenuList>

        <Divider />

        <MenuList dense disablePadding>
          <MenuItem
            onClick={onLogout}
            sx={{
              ...menuItemSx,
              color: 'error.main',
              '& .MuiSvgIcon-root': { color: 'error.main' },
            }}
          >
            <LogoutOutlinedIcon fontSize="small" />
            {t('verbs.logout')}
          </MenuItem>
        </MenuList>
      </Menu>
      <ContactUsDialog open={contactOpen} onClose={() => setContactOpen(false)} />
    </>
  );
};

const mapDispatchToProps = dispatch => ({
  clearState() {
    dispatch(clearAll());
  },
});

const ConnectedComponent = connect(
  null,
  mapDispatchToProps
)(ProfilePictureWithDropdown);

export default ConnectedComponent;
